import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check-icon.svg';

// ─── SelectDropdown ───────────────────────────────────────────────────────────
// Single-select sibling of MultiSelectDropdown — same trigger/icon/styles and
// body-portal positioning, but picks one value (no checkboxes / apply footer).
// Props:
//   label       {string}    Optional prefix label e.g. "Промо"
//   placeholder {string}    Text when nothing is selected
//   options     {Array}     [{ value, label, icon? }] or ["A", "B"]
//   value       {string}    Currently selected value
//   onChange    {Function}  Called with the picked value
const SelectDropdown = ({ label, options = [], placeholder = "Выберите", value, onChange, filterSelectStyle = null }) => {
  const normalizedOptions = options ? options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  ) : [];

  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ placement: "bottom", top: 0, bottom: "auto", left: 0, width: 0, maxHeight: 0 });

  // Pin the portal menu to the trigger so no ancestor with overflow can clip it.
  // Flip above the trigger when there's more room there.
  const updateMenuPosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const gap = 6;
    const margin = 8;
    const preferred = 360;

    const width = rect.width;
    const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));

    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const openUp = spaceBelow < preferred && spaceAbove > spaceBelow;
    const space = openUp ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(preferred, Math.max(120, space));

    if (openUp) {
      setMenuPos({ placement: "top", top: "auto", bottom: window.innerHeight - rect.top + gap, left, width, maxHeight });
    } else {
      setMenuPos({ placement: "bottom", top: rect.bottom + gap, bottom: "auto", left, width, maxHeight });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const handle = () => updateMenuPosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, updateMenuPosition]);

  // Close on outside click. The menu lives in a body portal, so clicks inside it
  // must also count as "inside" (check both refs).
  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = normalizedOptions.find((o) => String(o.value) === String(value));
  const displayLabel = selected ? selected.label : "";

  const handleSelect = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", ...filterSelectStyle }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="filter-select"
        style={{ width: "100%" }}
      >
        {label && <span className="filter-select-label">{label}:</span>}
        <span className="filter-select-option" style={{ flex: 1, color: selected ? "#1a1a1a" : "#9a9a9a", maxWidth: "150px" }}>
          {displayLabel || placeholder}
        </span>
        <ArrowDownIcon size={24} color='#7CC805' />
      </button>

      {open && createPortal(
        <div ref={menuRef} className="dropdown-portal" style={{
          position: "fixed", top: menuPos.top, bottom: menuPos.bottom, left: menuPos.left, minWidth: menuPos.width,
          maxHeight: menuPos.maxHeight, display: "flex", flexDirection: "column",
          background: "#ffffff", border: "1.5px solid #d1d5db", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 3000, overflow: "hidden",
          animation: "dropIn 0.15s ease",
        }}>
          <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
            {normalizedOptions.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <div key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", background: isSelected ? "rgba(124,200,5,0.06)" : "transparent", transition: "background 0.12s", fontSize: 14, fontWeight: isSelected ? 600 : 400, color: "#1a1a1a" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(124,200,5,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "rgba(124,200,5,0.06)" : "transparent")}
                >
                  {opt.icon && <span style={{ fontSize: 16 }}>{opt.icon}</span>}
                  <span style={{ flex: 1 }}>{opt.label}</span>
                  {isSelected && <CheckIcon color="#7CC805" width={16} height={16} />}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SelectDropdown;
